import {
  BracketsCurlyIcon,
  ChatsCircleIcon,
  GaugeIcon,
  GavelIcon,
  ImagesIcon,
  ListChecksIcon,
  MagnifyingGlassIcon,
  MoonIcon,
  ScrollIcon,
  SignOutIcon,
  SunIcon,
  UsersThreeIcon
} from "@phosphor-icons/react";
import type { ComponentType, ReactNode } from "react";
import type { User } from "../types";

export type Section = "dashboard" | "chats" | "users" | "logs" | "votes" | "lists" | "patterns" | "image-cache";

type Props = {
  user: User;
  section: Section;
  children: ReactNode;
  dark: boolean;
  onNavigate: (path: string) => void;
  onToggleTheme: () => void;
  onLogout: () => void;
};

type NavItem = { section: Section; label: string; path: string; icon: ComponentType<{ size?: number; weight?: "regular" | "bold"; "aria-hidden"?: boolean }> };

const baseNav: NavItem[] = [
  { section: "dashboard", label: "Обзор", path: "/dashboard", icon: GaugeIcon },
  { section: "chats", label: "Чаты", path: "/chats", icon: ChatsCircleIcon },
  { section: "users", label: "Пользователи", path: "/users", icon: MagnifyingGlassIcon },
  { section: "logs", label: "Журналы", path: "/logs", icon: ScrollIcon },
  { section: "votes", label: "Голосования", path: "/votes", icon: GavelIcon }
];

const superNav: NavItem[] = [
  { section: "lists", label: "Списки", path: "/admin/lists", icon: UsersThreeIcon },
  { section: "patterns", label: "Паттерны", path: "/admin/patterns", icon: BracketsCurlyIcon },
  { section: "image-cache", label: "Кеш изображений", path: "/admin/image-cache", icon: ImagesIcon }
];

export function Shell({ user, section, children, dark, onNavigate, onToggleTheme, onLogout }: Props) {
  const items = user.is_super_admin ? [...baseNav, ...superNav] : baseNav;
  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">К содержимому</a>
      <header className="command-bar">
        <button className="brand" onClick={() => onNavigate("/dashboard")} aria-label="На главную">
          <img className="brand-logo" src="/guvenlik-poster.webp" alt="" />
          <span className="brand-copy"><strong>ChatGuvenlik</strong><small>панель модерации</small></span>
        </button>
        <nav className="primary-nav" aria-label="Основная навигация">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.section} className={section === item.section ? "active" : ""} onClick={() => onNavigate(item.path)} aria-label={item.label} aria-current={section === item.section ? "page" : undefined}>
                <Icon aria-hidden={true} size={17} weight={section === item.section ? "bold" : "regular"} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="account-actions">
          <span className="role-stamp"><ListChecksIcon size={15} />{user.is_super_admin ? "SUPER" : "CHAT"}</span>
          <button className="icon-button" onClick={onToggleTheme} aria-label={dark ? "Включить светлую тему" : "Включить тёмную тему"}>
            {dark ? <SunIcon size={18} /> : <MoonIcon size={18} />}
          </button>
          <div className="account">
            <div className="avatar">
              {user.photo_url ? <img src={user.photo_url} alt={`Фото ${user.first_name}`} /> : user.first_name.slice(0, 1).toUpperCase()}
            </div>
            <div><strong>{user.first_name}</strong><small>{user.username ? `@${user.username}` : `ID ${user.id}`}</small></div>
          </div>
          <button className="icon-button" onClick={onLogout} aria-label="Выйти"><SignOutIcon size={18} /></button>
        </div>
      </header>
      <main className="content" id="main-content">{children}</main>
    </div>
  );
}
